import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo } from 'react'
import styled from 'styled-components'

import { Button, CloseIcon } from '@blade/ui-kit'
import { USER_TRADING_ANALYTICS_PERIOD_LABEL_MAP } from '@root/features/user/user-trading-analytics/models'
import { useTranslation } from '@root/shared/localization'
import { DtDrawer, SegmentControl } from '@root/shared/ui-kit'

import { distributeRangeAtom, reatomDistributeTable } from '../atoms'
import { DISTRIBUTE_RANGES, distributeRangeId } from '../model'

import { DistributeTable } from './distribute-table/distribute-table'

import type { DelugeId } from '@root/entities/user-short'
import type { UserTradingAnalyticsPeriod } from '@root/features/user/user-trading-analytics/models'
import type { ClassNameProps } from '@root/shared/react'

type DistributeDrawerDtProps = {
  period: UserTradingAnalyticsPeriod
  delugeId: DelugeId
} & ClassNameProps

export const DistributeDrawerDt: React.FC<DistributeDrawerDtProps> = ({
  period,
  delugeId,
  className,
}) => {
  const { t } = useTranslation()
  const [distributeRange, setDistributeRange] = useAtom(distributeRangeAtom)

  const model = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomDistributeTable(delugeId),
    [delugeId],
  )
  const [, setPeriod] = useAtom(model.periodAtom)
  const load = useAction(model.tableModel.load)

  useEffect(() => {
    setPeriod(period)
  }, [period, setPeriod])

  useEffect(() => {
    if (!distributeRange) {
      return
    }

    load()
  }, [distributeRange, period, load])

  const handleClose = () => setDistributeRange(undefined)
  const handleOpenChange = (open: boolean) => {
    setDistributeRange(open ? distributeRange : undefined)
  }

  return (
    <Root
      overlay
      side="right"
      open={distributeRange !== undefined}
      className={className}
      onOpenChange={handleOpenChange}
    >
      <Header>
        <Title>
          <span>{t(USER_TRADING_ANALYTICS_PERIOD_LABEL_MAP[period])}</span>{' '}
          {t('profile.distribute-drawer.token-distribution')}
        </Title>
        <Button
          variant="ghost"
          type="button"
          iconLeft={<CloseIcon />}
          onClick={handleClose}
          size="xs"
        />
      </Header>
      <Body>
        <SegmentControl
          getId={distributeRangeId}
          value={distributeRange ?? [-Infinity, Infinity]}
          onChange={setDistributeRange}
        >
          {DISTRIBUTE_RANGES.map(([range, label]) => (
            <SegmentControl.Item key={distributeRangeId(range)} value={range}>
              {label === 'ALL' ? t('common.all') : label}
            </SegmentControl.Item>
          ))}
        </SegmentControl>
      </Body>
      <DistributeTable model={model} onClose={handleClose} />
    </Root>
  )
}

const Root = styled(DtDrawer)`
  width: 600px;

  display: grid;
  grid-template-rows: auto auto 1fr;
  gap: 10px;

  background: var(--background-1);
`

const Body = styled.div`
  padding: 0 15px;
`

const Header = styled.div`
  border-bottom: 1px solid var(--background-3);

  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;

  padding: 10px 15px;
`

const Title = styled.div`
  color: var(--text-11);

  display: flex;
  align-items: center;

  gap: 4px;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;

  span {
    text-transform: uppercase;
  }
`
